import React, { useMemo, useState } from 'react';
import { Users, Plus, RefreshCw, UserCheck, Video } from 'lucide-react';
import { Modal, Button } from 'react-bootstrap';
import { UnifiedSlot } from '../InstructorDashboard';
import SlotActionBar from './SlotActionBar';
import AddResourceModal from './AddResourceModal';
import ViewAttendanceModal from './ViewAttendanceModal';
import ApiService from '../../../_services/ApiService';

// OnlineMeetingType 44 = Zoom Meeting (per Tuningfork Constants.StudyResourceConstant).
// Teams (45) reconcile is a follow-up — it uses the searchKey Lambda flow.
const ZOOM_MEETING_TYPE_ID = 44;

// Which half was invoked — drives spinner placement and result-modal trimming.
// Matches Java's separation of ZoomMeetingAttendanceListener and ZoomMeetingVideoUploadListener,
// which were fused only in the manual admin API. Node keeps them as two distinct buttons and
// leaves per-tenant "show which button" gating for a follow-up config change.
type ReconcileHalf = 'attendance' | 'video';

interface ReconcileSummary {
  title: string;
  videoLine: string;
  attendanceLine: string;
  isError: boolean;
  authUrl?: string;
}

// Human-readable one-liner for each server-returned video status.
function describeVideo(video: any): string {
  if (!video) return '—';
  switch (video.status) {
    case 'attached':                       return `Video: attached — ${video.message ?? ''}`.trim();
    case 'already_attached':               return `Video: already attached (no changes).`;
    case 'no_recording':                   return `Video: no recording available yet on Zoom.`;
    case 'skipped_no_meeting':             return `Video: no Zoom meeting on this slot.`;
    case 'skipped_vimeo_not_configured':   return `Video: Vimeo not configured for this tenant.`;
    case 'skipped_disabled':               return `Video: skipped.`;
    case 'failed':                         return `Video failed: ${video.message ?? 'unknown error'}`;
    default:                               return `Video: ${video.status ?? 'unknown'}`;
  }
}

function describeAttendance(attendance: any): string {
  if (!attendance) return '—';
  switch (attendance.status) {
    case 'synced':                return `Attendance: marked ${attendance.presentRecorded ?? 0} present from ${attendance.participantsFound ?? 0} Zoom participant(s).`;
    case 'no_participants':       return `Attendance: Zoom returned no participants (past-meeting report may not be available).`;
    case 'skipped_no_meeting':    return `Attendance: no Zoom meeting on this slot.`;
    case 'skipped_disabled':      return `Attendance: skipped.`;
    case 'failed':                return `Attendance failed: ${attendance.message ?? 'unknown error'}`;
    default:                      return `Attendance: ${attendance.status ?? 'unknown'}`;
  }
}

interface DashboardAgendaViewProps {
  slots: UnifiedSlot[];
  onDayClick: (date: string) => void;
  // Extra params carry slot instructor info — required by the admin flow to
  // build a /attendance-node URL with ?instructorId=&instructorName=. Instructor
  // flow accepts them harmlessly. See openAttendanceForBatches for the rationale.
  onAttendance: (
    batchIds: number[],
    templateSlotId?: number,
    instructorId?: number,
    instructorName?: string,
  ) => void;
  onLessonPlan: (batchId: number, lessonPlanId: number, topicId?: number) => void;
  onViewTemplate: (templateId: number) => void;
  onConfirm?: (slot: UnifiedSlot) => void;
  onUnconfirm?: (slot: UnifiedSlot) => void;
  onMeetingStarted?: () => void;
  selectedSlotIds?: number[];
  onToggleSelect?: (slotId: number) => void;
  /**
   * Force-hide the "Timetable" action on every slot's SlotActionBar. Set true
   * when rendering this component inside a page that IS the timetable detail
   * (e.g. TemplateConductedView on the admin's /schedules-templates/:id page).
   */
  hideViewTemplateButton?: boolean;
}

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatTimeDisplay(time: string): string {
  if (!time) return '-';
  const parts = time.split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1];
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m} ${ampm}`;
}

const todayKey = toDateKey(new Date());

const DashboardAgendaView: React.FC<DashboardAgendaViewProps> = ({
  slots,
  onDayClick,
  onAttendance,
  onLessonPlan,
  onViewTemplate,
  onConfirm,
  onUnconfirm,
  onMeetingStarted,
  selectedSlotIds,
  onToggleSelect,
  hideViewTemplateButton = false,
}) => {
  const [addResourceSlotId, setAddResourceSlotId] = useState<number | null>(null);
  const [viewAttendanceSlotId, setViewAttendanceSlotId] = useState<number | null>(null);
  const [reconcilingSlotId, setReconcilingSlotId] = useState<number | null>(null);
  const [reconcilingHalf, setReconcilingHalf] = useState<ReconcileHalf | null>(null);
  const [reconcileSummary, setReconcileSummary] = useState<ReconcileSummary | null>(null);

  // ── Attendance sync ──────────────────────────────────────────────────────
  // Mirrors Java's ZoomMeetingAttendanceListener. Owns its own payload, its own
  // response shape, its own error branches. Deliberately not sharing code with
  // the video handler so each can evolve independently — attendance may later
  // grow duration-threshold / fuzzy-match options that video doesn't need.
  const handleSyncAttendance = async (slot: UnifiedSlot) => {
    if (!slot.actualSlotId) return;
    setReconcilingSlotId(slot.actualSlotId);
    setReconcilingHalf('attendance');
    try {
      const response = await ApiService.syncZoomAttendanceForActivitySlot(slot.actualSlotId);
      const data = response?.data ?? {};
      // Peer endpoint returns the attendance result directly on data.result — no sub-object.
      const attendance = data.result;
      const failed = attendance?.status === 'failed';

      setReconcileSummary({
        title: failed ? 'Attendance sync failed' : 'Attendance sync complete',
        videoLine: '',
        attendanceLine: describeAttendance(attendance),
        isError: failed,
      });
    } catch (err: any) {
      const body = err?.response?.data;
      if (body?.error === 'zoom_auth_required') {
        setReconcileSummary({
          title: 'Zoom authorization required',
          videoLine: '',
          attendanceLine: body.message ?? 'Please re-authorize Zoom and try again.',
          isError: true,
          authUrl: body.authUrl,
        });
      } else {
        setReconcileSummary({
          title: 'Attendance sync failed',
          videoLine: '',
          attendanceLine: body?.message ?? err?.message ?? 'Unknown error.',
          isError: true,
        });
      }
    } finally {
      setReconcilingSlotId(null);
      setReconcilingHalf(null);
    }
  };

  // ── Video sync (Zoom recording → Vimeo) ──────────────────────────────────
  // Mirrors Java's ZoomMeetingVideoUploadListener. Independent from attendance:
  // may later gain forceVideoRefresh / vimeoConfig-override toggles that don't
  // apply to attendance. On a successful attach it also triggers the dashboard
  // refresh so the newly-linked study resource shows up in the timeline.
  const handleSyncVideo = async (slot: UnifiedSlot) => {
    if (!slot.actualSlotId) return;
    setReconcilingSlotId(slot.actualSlotId);
    setReconcilingHalf('video');
    try {
      const response = await ApiService.syncZoomVideoForActivitySlot(slot.actualSlotId);
      const data = response?.data ?? {};
      // Peer endpoint returns the video result directly on data.result — no sub-object.
      const video = data.result;
      const failed = video?.status === 'failed';
      const attached = video?.status === 'attached';

      setReconcileSummary({
        title: failed ? 'Video sync failed' : 'Video sync complete',
        videoLine: describeVideo(video),
        attendanceLine: '',
        isError: failed,
      });
      if (attached && onMeetingStarted) onMeetingStarted();
    } catch (err: any) {
      const body = err?.response?.data;
      if (body?.error === 'zoom_auth_required') {
        setReconcileSummary({
          title: 'Zoom authorization required',
          videoLine: body.message ?? 'Please re-authorize Zoom and try again.',
          attendanceLine: '',
          isError: true,
          authUrl: body.authUrl,
        });
      } else {
        setReconcileSummary({
          title: 'Video sync failed',
          videoLine: body?.message ?? err?.message ?? 'Unknown error.',
          attendanceLine: '',
          isError: true,
        });
      }
    } finally {
      setReconcilingSlotId(null);
      setReconcilingHalf(null);
    }
  };

  const groupedByDate = useMemo(() => {
    const map = new Map<string, UnifiedSlot[]>();
    for (const slot of slots) {
      if (!slot.date) continue;
      const existing = map.get(slot.date) || [];
      existing.push(slot);
      map.set(slot.date, existing);
    }
    map.forEach((daySlots) => {
      daySlots.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
    });
    const sortedDates = Array.from(map.keys()).sort();
    return sortedDates.map(date => ({ date, slots: map.get(date)! }));
  }, [slots]);

  return (
    <div className="idash-agenda-view">
      <div className="idash-agenda-header">
        <span className="idash-agenda-title">Agenda</span>
        <span className="idash-agenda-count">
          {slots.length} slot{slots.length !== 1 ? 's' : ''} across {groupedByDate.length} day{groupedByDate.length !== 1 ? 's' : ''}
        </span>
      </div>

      {slots.length === 0 ? (
        <div className="idash-agenda-empty">No slots found for the selected filters.</div>
      ) : (
        <div className="idash-agenda-table">
          <div className="idash-agenda-table-header">
            <div className="agenda-col-date">DATE</div>
            <div className="agenda-col-planned">PLANNED</div>
            <div className="agenda-col-conducted">CONDUCTED</div>
          </div>

          {groupedByDate.map(({ date, slots: daySlots }) => {
            const dateObj = parseLocalDate(date);
            const isToday = date === todayKey;
            return (
              <div key={date} className="idash-agenda-date-group">
                <div
                  className={`agenda-date-cell ${isToday ? 'is-today' : ''}`}
                  onClick={() => onDayClick(date)}
                >
                  <span className="agenda-date-num">{dateObj.getDate()}</span>
                  <span className="agenda-date-day">
                    {dateObj.toLocaleDateString('en-US', { weekday: 'long' })}
                  </span>
                  <span className="agenda-date-month">
                    {dateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                </div>

                <div className="agenda-slots-column">
                  {daySlots.map((slot) => {
                    const hasActual = !!slot.actualSlotId;
                    const timeDiffers = hasActual && (slot.actualStartTime !== slot.startTime || slot.actualEndTime !== slot.endTime);
                    const topicDiffers = hasActual && slot.actualTopicId !== null && slot.actualTopicId !== slot.topicMasterId;
                    const instructorDiffers = hasActual && slot.actualInstructorId !== null && slot.actualInstructorId !== slot.instructorId;
                    const batchesDiffer = hasActual && (slot.actualBatchIds || []).length > 0
                      && ((slot.actualBatchIds || []).length !== slot.batchIds.length
                          || (slot.actualBatchIds || []).some(id => !slot.batchIds.includes(id)));

                    const isConfirmable = slot.confirmedByUserId == null && slot.status !== 'cancelled';
                    const isSelected = !!selectedSlotIds?.includes(slot.templateSlotId);

                    return (
                      <div key={slot.templateSlotId} className={`idash-agenda-slot-row status-${slot.status}`}>
                        <div className="agenda-planned-cell">
                          {onToggleSelect && isConfirmable && (
                            <label className="agenda-slot-select" title="Select to confirm">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => onToggleSelect(slot.templateSlotId)}
                              />
                            </label>
                          )}
                          <div className="agenda-slot-time">
                            {formatTimeDisplay(slot.startTime)} - {formatTimeDisplay(slot.endTime)}
                          </div>
                          {slot.batchNames.length > 0 && (
                            <div className="agenda-slot-batch">{slot.batchNames.join(', ')}</div>
                          )}
                          <div className="agenda-slot-topic">{slot.topicName || slot.templateName || '-'}</div>
                          <div className="agenda-slot-instructor">{slot.instructorName || '-'}</div>
                          <div className="agenda-slot-actions">
                            <SlotActionBar
                              slot={slot}
                              compact={false}
                              onAttendance={onAttendance}
                              onLessonPlan={onLessonPlan}
                              onViewTemplate={onViewTemplate}
                              onConfirm={onConfirm}
                              onUnconfirm={onUnconfirm}
                              hideViewTemplateButton={hideViewTemplateButton}
                            />
                          </div>
                        </div>

                        <div className="agenda-conducted-cell">
                          {hasActual ? (
                            <>
                              <div className={`agenda-slot-time ${timeDiffers ? 'agenda-diff' : ''}`}>
                                {formatTimeDisplay(slot.actualStartTime || slot.startTime)} - {formatTimeDisplay(slot.actualEndTime || slot.endTime)}
                              </div>
                              {((slot.actualBatchNames || []).length > 0 || slot.batchNames.length > 0) && (
                                <div className={`agenda-slot-batch ${batchesDiffer ? 'agenda-diff' : ''}`}>
                                  {(slot.actualBatchNames || []).length > 0 ? slot.actualBatchNames.join(', ') : slot.batchNames.join(', ')}
                                </div>
                              )}
                              <div className={`agenda-slot-topic ${topicDiffers ? 'agenda-diff' : ''}`}>
                                {slot.actualTopicName || slot.topicName || slot.templateName || '-'}
                              </div>
                              <div className={`agenda-slot-instructor ${instructorDiffers ? 'agenda-diff' : ''}`}>
                                {slot.actualInstructorName || slot.instructorName || '-'}
                              </div>
                              {slot.attendanceRate !== null && (
                                <div className="agenda-slot-attendance">
                                  Attendance: {slot.presentCount}/{slot.studentCount} ({slot.attendanceRate}%)
                                </div>
                              )}
                              <div className="agenda-conducted-actions">
                                {slot.actualSlotId !== null && (
                                  <button
                                    type="button"
                                    className="agenda-conducted-action-btn"
                                    title="Student Attendance"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setViewAttendanceSlotId(slot.actualSlotId);
                                    }}
                                  >
                                    <Users size={18} />
                                  </button>
                                )}
                                {slot.actualSlotId !== null && (
                                  <button
                                    type="button"
                                    className="agenda-conducted-action-btn"
                                    title="Add Resource"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setAddResourceSlotId(slot.actualSlotId);
                                    }}
                                  >
                                    <Plus size={18} />
                                  </button>
                                )}
                                {slot.actualSlotId !== null
                                  && slot.onlineMeetingType === ZOOM_MEETING_TYPE_ID
                                  && slot.zoomMeetingId && (
                                  <>
                                    <button
                                      type="button"
                                      className="agenda-conducted-action-btn"
                                      title="Sync Zoom attendance"
                                      disabled={reconcilingSlotId === slot.actualSlotId}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSyncAttendance(slot);
                                      }}
                                    >
                                      {reconcilingSlotId === slot.actualSlotId && reconcilingHalf === 'attendance'
                                        ? <RefreshCw size={18} className="idash-spin" />
                                        : <UserCheck size={18} />}
                                    </button>
                                    <button
                                      type="button"
                                      className="agenda-conducted-action-btn"
                                      title="Sync Zoom recording to Vimeo"
                                      disabled={reconcilingSlotId === slot.actualSlotId}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSyncVideo(slot);
                                      }}
                                    >
                                      {reconcilingSlotId === slot.actualSlotId && reconcilingHalf === 'video'
                                        ? <RefreshCw size={18} className="idash-spin" />
                                        : <Video size={18} />}
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          ) : (
                            <div className="agenda-slot-not-conducted">
                              <span className={`agenda-status-badge ${slot.status}`}>{slot.status}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <AddResourceModal
        show={addResourceSlotId !== null}
        activitySlotId={addResourceSlotId}
        onClose={() => setAddResourceSlotId(null)}
        onSuccess={() => {
          if (onMeetingStarted) onMeetingStarted();
        }}
      />
      <ViewAttendanceModal
        show={viewAttendanceSlotId !== null}
        activitySlotId={viewAttendanceSlotId}
        onClose={() => setViewAttendanceSlotId(null)}
      />
      <Modal show={reconcileSummary !== null} onHide={() => setReconcileSummary(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{reconcileSummary?.title ?? ''}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reconcileSummary?.videoLine && (
            <p style={{ marginBottom: 8 }}>{reconcileSummary.videoLine}</p>
          )}
          {reconcileSummary?.attendanceLine && (
            <p style={{ marginBottom: reconcileSummary?.authUrl ? 12 : 0 }}>
              {reconcileSummary.attendanceLine}
            </p>
          )}
          {reconcileSummary?.authUrl && (
            <a href={reconcileSummary.authUrl} target="_blank" rel="noopener noreferrer">
              Click here to re-authorize Zoom
            </a>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant={reconcileSummary?.isError ? 'danger' : 'primary'}
            onClick={() => setReconcileSummary(null)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardAgendaView;
